import { Component } from "@theme/component";
import {
  ThemeEvents,
  VariantSelectedEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { morph } from "@theme/morph";
import { requestYieldCallback } from "@theme/utilities";

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {(event: Event) => void} */
  #handleChange = (event) => this.variantChanged(event);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("change", this.#handleChange);
    this.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);

    const subscriptionContainer =
      document.querySelector(".shopify_subscriptions_app_container") ||
      document.querySelector(".shopify_subscriptions_fieldset");
    const subscriptionBlockParent = subscriptionContainer?.parentElement;

    function cleanupVariantPrices() {
      subscriptionContainer
        ?.querySelectorAll("input[data-variant-price]")
        .forEach((input) => {
          const price = input.getAttribute("data-variant-price");
          if (!price) return;

          // Match numeric price with optional decimals
          const match = price.match(/\$[\d,.]+/);
          if (match) {
            // Update the data attribute to just the numeric price
            input.setAttribute("data-variant-price", match[0]);
          }
        });
    }
    function cleanupPrices() {
      document
        .querySelectorAll(".shopify_subscriptions_in_widget_price")
        .forEach((el) => {
          const text = el.innerText.trim();
          const match = text.match(/\$[\d,.]+/); // match $15.00, $1,200.99, etc.
          if (match) {
            el.innerText = `${match[0]}`;
          }
        });
    }
    if (subscriptionContainer && subscriptionBlockParent) {
      subscriptionBlockParent.classList.add("w-full");

      const visibleBlocks = subscriptionContainer.querySelectorAll(
        ".shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)",
      );

      if (visibleBlocks.length > 0) {
        subscriptionBlockParent.classList.remove("hidden");

        // run cleanups only if subscription blocks are visible
        cleanupPrices();
        cleanupVariantPrices();
      } else {
        subscriptionBlockParent.classList.add("hidden");
      }
    } else {
      const noSubs = document.querySelector('[id*="subscriptions_app_block"]');
      if (noSubs) {
        noSubs.classList.add("hidden");
      }
    }

    // TODO: Re-enable subscription UI for FF in select environments
    // this.initSubscriptionUi();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("change", this.#handleChange);
    this.removeEventListener(
      ThemeEvents.variantUpdate,
      this.handleVariantUpdate,
    );
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#pendingRequestUrl = undefined;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    let selectedTarget = event.target;

    // if event comes from shade dropdown
    if (event.target.classList.contains("custom-select-radio")) {
      selectedTarget = document.querySelector(
        `.variant-option__button-label--has-swatch input[data-variant-id="${event.target.dataset.variantId}"]`,
      );
      const wrapper = event.target.closest(".custom-select-wrapper");
      const label = wrapper?.querySelector(".custom-select-selected");
      if (!wrapper || !label) return;
      label.textContent = event.target.value;
      // Close dropdown
      const options = wrapper.querySelector(".custom-select-options");
      if (options) options.removeAttribute("open");

      if (document.getElementById("tone-All")) {
        document.getElementById("tone-All").checked = true;
      }
    }

    this.updateSelectedOption(selectedTarget);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? "",
      }),
    );

    const sendVTOSkuEvent = new CustomEvent("vto:applyMakeupBySku", {
      detail: {
        sku: selectedOption.dataset.variantSku,
      },
    });
    document.dispatchEvent(sendVTOSkuEvent);

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Track which option was just selected so auto-select won't undo it
    const selectedFieldset = selectedOption.closest("fieldset");
    this.#lastSelectedFieldset = selectedFieldset;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      loadsNewProduct,
    );

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }
  }

  handleVariantUpdate = () => {
    requestYieldCallback(() => {
      // TODO: Re-enable subscription UI for FF in select environments
      // this.initSubscriptionUi();
    });
  };

  initSubscriptionUi() {
    const containers = document.querySelectorAll(
      ".shopify_subscriptions_app_container, .shopify_subscriptions_fieldset",
    );
    if (!containers.length) return;

    containers.forEach((container) => {
      const blocks = Array.from(
        container.querySelectorAll(".shopify_subscriptions_app_block"),
      );
      const labelBlocks = Array.from(
        container.querySelectorAll(".shopify_subscriptions_app_block_label"),
      );
      const childList = container.querySelector(
        ".shopify_subscriptions_app_block_label_children",
      );
      const subscriptionBlock =
        childList?.closest(".shopify_subscriptions_app_block") ||
        childList?.parentElement ||
        container;
      if (!subscriptionBlock) return;

      let purchaseOptions = [];
      if (blocks.length) {
        purchaseOptions = blocks
          .map((block) => ({
            block,
            radio: block.querySelector(
              'input[type="radio"][name*="purchaseOption"], input[type="radio"][name*="purchase_option"]',
            ),
            label: block.querySelector(
              ".shopify_subscriptions_app_block_label",
            ),
            isSubscription: block === subscriptionBlock,
          }))
          .filter((item) => item.radio || item.isSubscription);
      } else if (labelBlocks.length) {
        const oneTimeLabel = labelBlocks.find((label) =>
          label.querySelector('input[type="radio"]'),
        );
        const subscriptionLabel =
          labelBlocks.find(
            (label) => !label.querySelector('input[type="radio"]'),
          ) || labelBlocks[labelBlocks.length - 1];
        if (oneTimeLabel) {
          purchaseOptions.push({
            block: oneTimeLabel,
            radio: oneTimeLabel.querySelector('input[type="radio"]'),
            label: oneTimeLabel,
            isSubscription: false,
          });
        }
        if (subscriptionLabel && subscriptionLabel !== oneTimeLabel) {
          purchaseOptions.push({
            block: subscriptionLabel,
            radio: null,
            label: subscriptionLabel,
            isSubscription: true,
          });
        }
      }

      this.setupSubscriptionBlock(subscriptionBlock);
      this.syncSubscriptionBlocks(purchaseOptions, subscriptionBlock);

      if (!container.dataset.subscriptionDropdownListener) {
        container.dataset.subscriptionDropdownListener = "true";
        container.addEventListener("change", (event) => {
          const target = event.target;
          if (
            target instanceof HTMLInputElement &&
            target.type === "radio" &&
            (target.name.includes("purchaseOption") ||
              target.name.includes("purchase_option"))
          ) {
            this.syncSubscriptionBlocks(purchaseOptions, subscriptionBlock);
          }
        });
      }
    });
  }

  setupSubscriptionBlock(subscriptionBlock) {
    if (!(subscriptionBlock instanceof HTMLElement)) return;
    if (subscriptionBlock.dataset.subscriptionDropdownReady) return;

    const children = subscriptionBlock.querySelector(
      ".shopify_subscriptions_app_block_label_children",
    );
    if (!children) return;

    const planInputs = Array.from(
      children.querySelectorAll('input[type="radio"]'),
    );
    if (!planInputs.length) return;

    const labels = Array.from(
      subscriptionBlock.querySelectorAll(
        ".shopify_subscriptions_app_block_label",
      ),
    );
    const labelWrapper =
      labels.find((label) => !label.querySelector('input[type="radio"]')) ||
      labels[labels.length - 1] ||
      subscriptionBlock.querySelector(".shopify_subscriptions_app_block_label");
    const labelElement =
      labelWrapper?.tagName === "LABEL"
        ? labelWrapper
        : labelWrapper?.querySelector("label") || labelWrapper;
    const oneTimeRadio = subscriptionBlock.querySelector(
      'input[type="radio"][data-radio-type="one_time_purchase"]',
    );

    const hiddenInputs = document.createElement("div");
    hiddenInputs.className = "shopify_subscriptions_plan_inputs hidden";
    hiddenInputs.appendChild(children);
    subscriptionBlock.appendChild(hiddenInputs);

    const dropdown = document.createElement("div");
    dropdown.className = "shopify_subscriptions_dropdown hidden";
    const select = document.createElement("select");
    select.className = "shopify_subscriptions_dropdown_select";
    dropdown.appendChild(select);
    labelWrapper?.insertAdjacentElement("afterend", dropdown);

    const getPlanId = (input, index) =>
      input.dataset.sellingPlanId || input.value || input.id || `plan-${index}`;
    const planMap = new Map();

    select.innerHTML = "";
    planInputs.forEach((input, index) => {
      const planId = getPlanId(input, index);
      planMap.set(planId, input);
      const option = document.createElement("option");
      option.value = planId;
      const label = input.closest("label");
      option.textContent = label?.textContent?.trim() || input.value;
      select.appendChild(option);
    });

    select.addEventListener("change", () => {
      const next = planMap.get(select.value);
      if (next) {
        next.checked = true;
        next.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    const cancelText = document.createElement("p");
    cancelText.className = "shopify_subscriptions_cancel-text hidden";
    cancelText.textContent = "Never run out of product. Cancel anytime.";
    dropdown.insertAdjacentElement("beforebegin", cancelText);

    if (labelWrapper && !labelWrapper.querySelector('input[type="radio"]')) {
      const subscriptionRadio = document.createElement("input");
      subscriptionRadio.type = "radio";
      subscriptionRadio.className = "shopify_subscriptions_subscription_radio";
      subscriptionRadio.dataset.radioType = "selling_plan";
      if (oneTimeRadio?.name) {
        subscriptionRadio.name = `${oneTimeRadio.name}__proxy`;
      }
      // subscriptionRadio.setAttribute(
      //   "aria-label",
      //   "Subscribe to save 10% + free shipping",
      // );
      labelElement?.prepend(subscriptionRadio);
      subscriptionRadio.addEventListener("change", () => {
        if (!subscriptionRadio.checked) return;
        if (!planInputs.some((input) => input.checked)) {
          const first = planInputs[0];
          if (first) {
            first.checked = true;
            first.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
      });
    }

    subscriptionBlock.dataset.subscriptionDropdownReady = "true";
  }

  syncSubscriptionBlocks(purchaseOptions, subscriptionBlock) {
    const appContainer = subscriptionBlock.closest(
      ".shopify_subscriptions_app_container",
    );
    const productId = appContainer?.dataset.productId;
    const sellingPlanInputs = productId
      ? Array.from(
          document.querySelectorAll(
            `product-form-component[data-product-id="${productId}"] input[name="selling_plan"], ` +
              `.shopify-block input[name="selling_plan"].selected-selling-plan-id`,
          ),
        )
      : Array.from(
          (
            subscriptionBlock.closest(".shopify-block") || subscriptionBlock
          ).querySelectorAll(
            'input[name="selling_plan"].selected-selling-plan-id',
          ),
        );
    const dropdown = subscriptionBlock.querySelector(
      ".shopify_subscriptions_dropdown",
    );
    const cancelText = subscriptionBlock.querySelector(
      ".shopify_subscriptions_cancel-text",
    );
    const select = subscriptionBlock.querySelector(
      ".shopify_subscriptions_dropdown_select",
    );
    const planInputs = Array.from(
      subscriptionBlock.querySelectorAll(
        ".shopify_subscriptions_plan_inputs input[type='radio']",
      ),
    );
    const subscriptionRadio = subscriptionBlock.querySelector(
      ".shopify_subscriptions_subscription_radio",
    );
    const oneTimeRadio = subscriptionBlock.querySelector(
      'input[type="radio"][data-radio-type="one_time_purchase"]',
    );
    let hasSelectedPlan = planInputs.some((input) => input.checked);
    const selectedPlanId = select?.value;
    const matchedPlan = selectedPlanId
      ? planInputs.find(
          (input) => input.dataset.sellingPlanId === selectedPlanId,
        )
      : null;
    const oneTimeSelected = Boolean(oneTimeRadio?.checked);
    let subscriptionSelected =
      Boolean(subscriptionRadio?.checked) || hasSelectedPlan;

    if (oneTimeSelected) {
      if (subscriptionRadio?.checked) {
        subscriptionRadio.checked = false;
      }
      if (planInputs.length && hasSelectedPlan) {
        planInputs.forEach((input) => {
          input.checked = false;
        });
        hasSelectedPlan = false;
      }
      subscriptionSelected = false;
    } else {
      if (subscriptionSelected && oneTimeRadio?.checked) {
        oneTimeRadio.checked = false;
      }
      if (subscriptionSelected && planInputs.length && !hasSelectedPlan) {
        const nextPlan = matchedPlan || planInputs[0];
        if (nextPlan && !nextPlan.checked) {
          nextPlan.checked = true;
          nextPlan.dispatchEvent(new Event("change", { bubbles: true }));
          hasSelectedPlan = true;
        }
      }
      if (subscriptionRadio && hasSelectedPlan && !subscriptionRadio.checked) {
        subscriptionRadio.checked = true;
      }
      subscriptionSelected =
        Boolean(subscriptionRadio?.checked) || hasSelectedPlan;
    }

    if (subscriptionRadio && hasSelectedPlan && !subscriptionRadio.checked) {
      subscriptionRadio.checked = true;
    }

    if (dropdown) dropdown.classList.toggle("hidden", !subscriptionSelected);
    if (cancelText)
      cancelText.classList.toggle("hidden", !subscriptionSelected);

    if (select && planInputs.length) {
      const checkedPlan = planInputs.find((input) => input.checked);
      if (checkedPlan) {
        const planId =
          checkedPlan.dataset.sellingPlanId ||
          checkedPlan.value ||
          checkedPlan.id;
        if (planId) {
          select.value = planId;
        }
      } else {
        if (select.value === "") {
          select.value = select.options[0]?.value || "";
        }
      }
    }

    if (sellingPlanInputs.length) {
      const checkedPlan = planInputs.find((input) => input.checked);
      const fallbackPlanId = select?.value || "";
      const planId =
        checkedPlan?.dataset.sellingPlanId ||
        checkedPlan?.value ||
        checkedPlan?.id ||
        fallbackPlanId;
      sellingPlanInputs.forEach((input) => {
        input.value = subscriptionSelected ? planId : "";
      });
    }

    const allocationPrice = subscriptionBlock.querySelector(
      ".shopify_subscriptions_in_widget_price.allocation_price",
    );
    if (allocationPrice) {
      const checkedPlan = planInputs.find((input) => input.checked);
      const price =
        checkedPlan?.dataset.variantPrice ||
        checkedPlan?.getAttribute("data-variant-price");
      if (subscriptionSelected && price) {
        allocationPrice.textContent = price;
      }
    }

    purchaseOptions.forEach(({ block, radio, label, isSubscription }) => {
      const selected = isSubscription
        ? subscriptionSelected
        : Boolean(radio?.checked);
      block.classList.toggle(
        "shopify_subscriptions_option--selected",
        selected,
      );
      label?.classList.toggle(
        "shopify_subscriptions_option--selected",
        selected,
      );
    });
  }
  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`,
      );

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
  ) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (
      this.closest("quick-add-component") ||
      this.closest("swatches-variant-picker-component")
    ) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`,
        )?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(
                JSON.parse(textContent),
                this.selectedOptionId,
                {
                  html,
                  productId: this.dataset.productId ?? "",
                  newProduct,
                },
              ),
            );
          }
        }

        const vtobuttons = document.querySelectorAll("[data-vto-trigger]");
        vtobuttons.forEach((button) => {
          button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            const openVTOEvent = new CustomEvent("vto:openShadeFinder", {
              detail: {
                button: button,
              },
            });
            document.dispatchEvent(openVTOEvent);
          });
        });
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase(),
    );

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    // Auto-select available option if current selection is unavailable
    this.#autoSelectAvailableOption();

    return newProduct;
  }

  /**
   * The fieldset that was most recently selected by the user.
   * Used to avoid auto-selecting away from user's explicit choice.
   * @type {HTMLFieldSetElement | null}
   */
  #lastSelectedFieldset = null;

  /**
   * Checks if any checked option has no matching variant and auto-selects one that does.
   * This handles cases like switching colors where the current size combination doesn't exist.
   * Note: This does NOT auto-select for out-of-stock variants - only for non-existent combinations.
   * Note: This does NOT change the option the user just clicked - only adjusts OTHER options.
   */
  #autoSelectAvailableOption() {
    // Find all fieldsets (each represents an option like Color, Size)
    const fieldsets = this.querySelectorAll("fieldset");

    for (const fieldset of fieldsets) {
      // Skip the fieldset the user just clicked - we don't want to undo their selection
      // We need to match by data-component-index since the DOM was morphed
      if (
        this.#lastSelectedFieldset &&
        fieldset instanceof HTMLFieldSetElement
      ) {
        const lastIndex = this.#lastSelectedFieldset.dataset.componentIndex;
        const currentIndex = fieldset.dataset.componentIndex;
        if (lastIndex !== undefined && lastIndex === currentIndex) continue;
      }

      const checkedInput = fieldset.querySelector("input:checked");
      if (!(checkedInput instanceof HTMLInputElement)) continue;

      // Check if the variant combination doesn't exist
      // data-combination-exists explicitly tells us if this option value + all other selected values = valid variant
      // This is different from being out of stock - we only auto-select when the combination doesn't exist
      const combinationExists =
        checkedInput.dataset.combinationExists === "true";
      if (combinationExists) continue;

      // Find the first option where the combination exists (regardless of stock status)
      const validInput = fieldset.querySelector(
        'input[data-combination-exists="true"]:not(:checked)',
      );

      if (validInput instanceof HTMLInputElement) {
        // Auto-select the valid option
        validInput.checked = true;

        // Clear the last selected fieldset since we're making a corrective selection
        this.#lastSelectedFieldset = null;

        // Trigger a new fetch with the corrected selection
        this.fetchUpdatedSection(this.buildRequestUrl(validInput), false);

        // Only fix one fieldset per update to avoid multiple fetches
        break;
      }
    }

    // Clear the tracking after auto-select completes
    this.#lastSelectedFieldset = null;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      "select option[selected], fieldset input:checked",
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
